import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { Button } from "src/components/ui/Button";
import { Input } from "src/components/ui/Input";
import { ROUTES } from "src/routes/RouteConfig";
import {
  ArrowLeft,
  UserPlus,
  Mail,
  User,
  Phone,
  GraduationCap,
  BookOpen,
  Check,
  ShieldCheck,
  Send,
  Key,
  Info,
  CheckCircle2,
  Lock,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { useToast } from "src/components/ui/UseToast";

type AcademyRole = "student" | "tutor";
type InviteMethod = "automated" | "manual";

const CreateUser: React.FC = () => {
  const navigate = useNavigate();
  const { setTitle, setDescription, reset } = usePageHeaderStore();
  const { toast } = useToast();

  // Core Form State: Minimal Mandatory Fields
  const [role, setRole] = useState<AcademyRole>("student");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    tempPassword: "",
  });
  const [inviteMethod, setInviteMethod] = useState<InviteMethod>("automated");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setTitle("Create New User");
    setDescription(
      "Provision a new student or faculty tutor with minimal mandatory identity coordinates"
    );

    return () => reset();
  }, [setTitle, setDescription, reset]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isFormValid =
    formData.firstName.trim().length > 0 &&
    formData.lastName.trim().length > 0 &&
    formData.email.trim().length > 0 &&
    (inviteMethod === "automated" || formData.tempPassword.trim().length >= 8);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setLoading(true);

    try {
      console.log("Provisioning minimal mandatory user:", {
        role,
        ...formData,
        inviteMethod,
      });

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 800));

      const roleLabel = role === "student" ? "Student" : "Faculty Tutor";

      toast({
        title: "Account Provisioned Successfully",
        description:
          inviteMethod === "automated"
            ? `${roleLabel} account created for ${formData.firstName} ${formData.lastName}. An onboarding activation link has been sent to ${formData.email}.`
            : `${roleLabel} account created for ${formData.firstName} ${formData.lastName} with temporary credentials for manual orientation.`,
        variant: "default",
      });

      navigate("/admin/users/list");
    } catch (error) {
      toast({
        title: "Provisioning Failed",
        description: "Failed to create user account. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const initials =
    (formData.firstName.charAt(0) + (formData.lastName.charAt(0) || "")).toUpperCase() ||
    "U";

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-12">
      {/* Page Breadcrumb & Target Role Switcher */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-1">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <button
            type="button"
            onClick={() => navigate(ROUTES.ADMIN_USERS?.path || "/admin/users")}
            className="hover:text-purple-700 transition"
          >
            User Operations
          </button>
          <span>/</span>
          <button
            type="button"
            onClick={() => navigate(ROUTES.ADMIN_USERS_LIST?.path || "/admin/users/list")}
            className="hover:text-purple-700 transition"
          >
            User Directory
          </button>
          <span>/</span>
          <span className="font-semibold text-slate-800">Add New User</span>
        </div>

        {/* Target Role Quick Switcher */}
        <div className="flex items-center gap-2 self-stretch sm:self-auto justify-end">
          <span className="text-xs text-slate-400 font-medium">Target Role:</span>
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-100">
            <button
              type="button"
              onClick={() => setRole("student")}
              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                role === "student"
                  ? "bg-white text-purple-700 shadow-2xs font-bold border border-slate-200/80"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Student
            </button>
            <button
              type="button"
              onClick={() => setRole("tutor")}
              className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                role === "tutor"
                  ? "bg-white text-purple-700 shadow-2xs font-bold border border-slate-200/80"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Faculty Tutor
            </button>
          </div>
        </div>
      </div>

      {/* Main Grid: Form on Left (8 cols) & Vertical Lifecycle Pipeline on Right (4 cols) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* ============================================================== */}
        {/* LEFT COLUMN: FORM CONSOLE (8 COLS) */}
        {/* ============================================================== */}
        <div className="lg:col-span-8 bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
          {/* Card Header */}
          <div className="px-6 py-4 border-b border-slate-200 bg-[#faf9fe] flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center font-bold text-xs">
                <UserPlus size={18} />
              </div>
              <div>
                <h2 className="text-sm font-bold text-slate-900">
                  User Account Coordinates
                </h2>
                <p className="text-xs text-slate-500">
                  Minimal mandatory parameters required to establish platform identity
                </p>
              </div>
            </div>
            <span className="text-xs font-medium text-slate-400 font-mono">
              OP-PROVISION
            </span>
          </div>

          <form id="create-user-form" onSubmit={handleSubmit} className="p-6 sm:p-7 space-y-7">
            {/* STEP 1: SELECT ACADEMY ROLE */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-slate-900 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-600 text-white text-xs font-bold flex items-center justify-center">
                    1
                  </span>
                  Select Academy Role <span className="text-rose-500">*</span>
                </label>
                <span className="text-xs font-mono font-medium text-slate-400">
                  MANDATORY
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {/* Student Card */}
                <div
                  onClick={() => setRole("student")}
                  className={`p-3.5 rounded-xl border-2 cursor-pointer relative transition-all ${
                    role === "student"
                      ? "border-purple-600 bg-purple-50/25 shadow-xs"
                      : "border-slate-200 bg-white hover:border-slate-300 shadow-2xs"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="w-8 h-8 rounded-lg bg-amber-500 text-white flex items-center justify-center font-bold text-sm shadow-xs">
                      <GraduationCap size={18} />
                    </div>
                    <span
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-xs transition-colors ${
                        role === "student"
                          ? "bg-purple-600 text-white"
                          : "border border-slate-300 text-transparent"
                      }`}
                    >
                      <Check size={12} strokeWidth={3} />
                    </span>
                  </div>
                  <h4 className="font-bold text-slate-900 mt-2.5 text-sm">
                    Student Account
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    Learner profile. Self-completes Education Level, Learning Goals, and Date of Birth during onboarding prior to Course Enrollment.
                  </p>
                  <div className="mt-2.5 flex items-center gap-1.5">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                      Learner Portal
                    </span>
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-600">
                      Enrollment Ready
                    </span>
                  </div>
                </div>

                {/* Faculty Tutor Card */}
                <div
                  onClick={() => setRole("tutor")}
                  className={`p-3.5 rounded-xl border-2 cursor-pointer relative transition-all ${
                    role === "tutor"
                      ? "border-purple-600 bg-purple-50/25 shadow-xs"
                      : "border-slate-200 bg-white hover:border-slate-300 shadow-2xs"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-xs">
                      <BookOpen size={18} />
                    </div>
                    <span
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-xs transition-colors ${
                        role === "tutor"
                          ? "bg-purple-600 text-white"
                          : "border border-slate-300 text-transparent"
                      }`}
                    >
                      <Check size={12} strokeWidth={3} />
                    </span>
                  </div>
                  <h4 className="font-bold text-slate-900 mt-2.5 text-sm">
                    Faculty Tutor Account
                  </h4>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    Instructional faculty. Completes credential dossier during onboarding, then undergoes admin verification before going live.
                  </p>
                  <div className="mt-2.5 flex items-center gap-1.5">
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                      Faculty Portal
                    </span>
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                      Requires Verification
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-xs text-slate-400 flex items-center gap-1.5 pt-0.5">
                <ShieldCheck size={13} className="text-slate-400 shrink-0" />
                <span>
                  Notice: Administrative credentials require root vault delegation and cannot be granted via batch member onboarding.
                </span>
              </p>
            </div>

            <hr className="border-slate-100" />

            {/* STEP 2: MINIMAL MANDATORY IDENTITY FIELDS */}
            <div className="space-y-3.5">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-900 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-600 text-white text-xs font-bold flex items-center justify-center">
                    2
                  </span>
                  User Identity Coordinates
                </h3>
                <span className="text-xs text-slate-500 font-medium">
                  Minimal Mandatory Fields
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {/* First Name */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    First Name <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <User
                      size={15}
                      className="absolute left-3 top-2.5 text-slate-400 pointer-events-none"
                    />
                    <Input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange("firstName", e.target.value)}
                      className="pl-9 h-9 border-slate-300 focus:ring-2 focus:ring-purple-600 focus:border-purple-600 rounded-lg text-xs font-medium"
                      placeholder="e.g. Aarav"
                      required
                    />
                  </div>
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Last Name <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <User
                      size={15}
                      className="absolute left-3 top-2.5 text-slate-400 pointer-events-none"
                    />
                    <Input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange("lastName", e.target.value)}
                      className="pl-9 h-9 border-slate-300 focus:ring-2 focus:ring-purple-600 focus:border-purple-600 rounded-lg text-xs font-medium"
                      placeholder="e.g. Sharma"
                      required
                    />
                  </div>
                </div>

                {/* Email Address */}
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Primary Email Address <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail
                      size={15}
                      className="absolute left-3 top-2.5 text-slate-400 pointer-events-none"
                    />
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      className="pl-9 h-9 border-slate-300 focus:ring-2 focus:ring-purple-600 focus:border-purple-600 rounded-lg text-xs font-medium"
                      placeholder="user@domain.com"
                      required
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Unique login identifier; destination for account notifications
                  </p>
                </div>

                {/* Phone Number (Optional) */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-semibold text-slate-700">
                      Phone Number
                    </label>
                    <span className="text-xs text-slate-400 font-medium">
                      Optional
                    </span>
                  </div>
                  <div className="relative">
                    <Phone
                      size={15}
                      className="absolute left-3 top-2.5 text-slate-400 pointer-events-none"
                    />
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", e.target.value)}
                      className="pl-9 h-9 border-slate-300 focus:ring-2 focus:ring-purple-600 focus:border-purple-600 rounded-lg text-xs font-medium"
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    SMS reminder delivery (can be provided by user during onboarding)
                  </p>
                </div>
              </div>
            </div>

            <hr className="border-slate-100" />

            {/* STEP 3: INVITATION & ACTIVATION METHOD */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-900 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-600 text-white text-xs font-bold flex items-center justify-center">
                    3
                  </span>
                  Invitation & Activation Method
                </h3>
                <span className="text-xs text-slate-400 font-mono">
                  DISPATCH METHOD
                </span>
              </div>

              <div className="space-y-2">
                {/* Option A: Automated Email */}
                <label
                  onClick={() => setInviteMethod("automated")}
                  className={`p-3 rounded-lg border-2 flex items-start gap-3 cursor-pointer transition-all ${
                    inviteMethod === "automated"
                      ? "border-purple-600 bg-purple-50/20"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="invite_method"
                    checked={inviteMethod === "automated"}
                    onChange={() => setInviteMethod("automated")}
                    className="mt-0.5 text-purple-600 focus:ring-purple-600"
                  />
                  <div className="text-xs">
                    <span className="font-bold text-slate-900 flex items-center gap-1.5">
                      <Send size={14} className="text-purple-600" />
                      Send Automated Onboarding Invitation (Recommended)
                    </span>
                    <p className="text-slate-500 mt-0.5">
                      Dispatches an activation email containing a secure 72-hour cryptographic token. User sets password and self-completes onboarding on first login.
                    </p>
                  </div>
                </label>

                {/* Option B: Manual Credentials */}
                <label
                  onClick={() => setInviteMethod("manual")}
                  className={`p-3 rounded-lg border flex items-start gap-3 cursor-pointer transition-all ${
                    inviteMethod === "manual"
                      ? "border-purple-600 bg-purple-50/20"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="invite_method"
                    checked={inviteMethod === "manual"}
                    onChange={() => setInviteMethod("manual")}
                    className="mt-0.5 text-purple-600 focus:ring-purple-600"
                  />
                  <div className="text-xs flex-1">
                    <span className="font-bold text-slate-700 flex items-center gap-1.5">
                      <Key size={14} className="text-slate-500" />
                      Generate Temporary Credentials for Manual Handoff
                    </span>
                    <p className="text-slate-500 mt-0.5">
                      No automated email is sent. Admin sets a temporary password to communicate to the user verbally or via private channel.
                    </p>

                    {inviteMethod === "manual" && (
                      <div className="mt-2.5 max-w-sm">
                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                          Temporary Password <span className="text-rose-500">*</span>
                        </label>
                        <div className="relative">
                          <Lock
                            size={14}
                            className="absolute left-3 top-2.5 text-slate-400 pointer-events-none"
                          />
                          <Input
                            type="password"
                            value={formData.tempPassword}
                            onChange={(e) =>
                              handleInputChange("tempPassword", e.target.value)
                            }
                            placeholder="Min 8 characters (letters & numbers)"
                            className="pl-8 h-8 text-xs"
                            required={inviteMethod === "manual"}
                          />
                        </div>
                        <p className="text-xs text-amber-700 mt-1">
                          You will need to communicate this temporary password directly to the user.
                        </p>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>

            <hr className="border-slate-100" />

            {/* STEP 4: ONBOARDING HAND-OFF & INITIAL STATUS */}
            <div className="space-y-3.5">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-bold text-slate-900 flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-purple-600 text-white text-xs font-bold flex items-center justify-center">
                    4
                  </span>
                  {role === "student"
                    ? "Student Onboarding Handoff & Initial Status"
                    : "Faculty Tutor Onboarding Handoff & Initial Status"}
                </h3>
                <span className="text-xs text-slate-500 font-mono">
                  {inviteMethod === "automated"
                    ? "EMAIL ACTIVATION DISPATCH"
                    : "MANUAL CREDENTIAL ORIENTATION"}
                </span>
              </div>

              {role === "student" ? (
                /* STUDENT VARIANT */
                <div className="space-y-3.5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">
                        Initial Account Status
                      </span>
                      <div className="flex items-center gap-2 pt-0.5">
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-300">
                          Onboarding Pending
                        </span>
                        <span className="text-xs text-slate-500 font-mono">
                          (onboarding_completed: false)
                        </span>
                      </div>
                    </div>

                    <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">
                        Next Academic Stage
                      </span>
                      <div className="flex items-center gap-2 pt-0.5">
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 flex items-center gap-1">
                          <GraduationCap size={13} className="text-purple-600" />
                          Course Enrollment
                        </span>
                        <span className="text-xs text-slate-500">
                          Self or Admin Manual Enrollment
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Student Hand-off Checklist */}
                  <div className="p-3.5 rounded-xl bg-amber-50/40 border border-amber-200/80 flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg bg-amber-500 text-white flex items-center justify-center shrink-0 font-bold text-xs mt-0.5 shadow-2xs">
                      <GraduationCap size={16} />
                    </div>
                    <div className="flex-1 text-xs space-y-1.5">
                      <div className="flex items-center justify-between">
                        <p className="font-bold text-amber-900">
                          {inviteMethod === "automated"
                            ? "Student Self-Registration Checklist (Automatic Onboarding)"
                            : "Student Onboarding Checklist (Manual Handoff Flow)"}
                        </p>
                        <span className="text-xs font-medium text-amber-800 bg-amber-100/70 px-2 py-0.5 rounded">
                          User-Completed
                        </span>
                      </div>

                      <p className="text-amber-800/90 leading-relaxed">
                        {inviteMethod === "automated" ? (
                          <>
                            Upon creation, an invitation email with a secure activation link is dispatched to{" "}
                            <span className="font-mono font-semibold text-slate-800">
                              {formData.email || "the student's email"}
                            </span>
                            . The learner will click the link, set their permanent password, and complete these required onboarding steps on first login:
                          </>
                        ) : (
                          <>
                            No automated email will be sent. Securely communicate the temporary password to{" "}
                            <span className="font-mono font-semibold text-slate-800">
                              {formData.email || "the student"}
                            </span>
                            . When the learner logs in with these credentials, they will be prompted to update their password and complete these required steps:
                          </>
                        )}
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1 text-xs">
                        <div className="bg-white p-2 rounded-lg border border-amber-200 text-slate-700 space-y-0.5">
                          <span className="font-bold text-amber-900 block flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>{" "}
                            Step 1: Education Level *
                          </span>
                          <span className="text-slate-500">
                            Primary, High School, College level selection
                          </span>
                        </div>
                        <div className="bg-white p-2 rounded-lg border border-amber-200 text-slate-700 space-y-0.5">
                          <span className="font-bold text-amber-900 block flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>{" "}
                            Step 2: Learning Goals *
                          </span>
                          <span className="text-slate-500">
                            Exam preparation, Concept mastery targets
                          </span>
                        </div>
                        <div className="bg-white p-2 rounded-lg border border-amber-200 text-slate-700 space-y-0.5">
                          <span className="font-bold text-amber-900 block flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>{" "}
                            Step 3: Date of Birth *
                          </span>
                          <span className="text-slate-500">
                            Day / Month / Year birthday verification &{" "}
                            {inviteMethod === "automated"
                              ? "password creation"
                              : "password update"}
                          </span>
                        </div>
                      </div>

                      <div className="mt-2.5 p-2.5 rounded-lg bg-white border border-amber-200 flex items-start gap-2">
                        <div className="w-4 h-4 rounded-md bg-amber-100 text-amber-800 flex items-center justify-center shrink-0 mt-0.5">
                          <Info size={12} />
                        </div>
                        <div className="text-xs text-slate-600 leading-relaxed">
                          <strong className="text-amber-900">
                            Subsequent Enrollment Flow:
                          </strong>{" "}
                          Once onboarding is complete, the student can browse courses and self-enroll, or an administrator can enroll the student directly via{" "}
                          <span className="font-semibold text-slate-800">
                            Manual Enrollment
                          </span>{" "}
                          (<code className="font-mono text-purple-700">/admin/enrollments/create</code>) to select products, curriculum scopes, and payment terms on their behalf.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* TUTOR VARIANT */
                <div className="space-y-3.5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">
                        Initial Faculty Status
                      </span>
                      <div className="flex items-center gap-2 pt-0.5">
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                          Onboarding Pending
                        </span>
                        <span className="text-xs text-slate-500 font-mono">
                          (onboarding_completed: false)
                        </span>
                      </div>
                    </div>

                    <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
                      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block">
                        Next Stage: Verification & Go-Live
                      </span>
                      <div className="flex items-center gap-2 pt-0.5">
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 flex items-center gap-1">
                          <CheckCircle2 size={13} className="text-purple-600" />
                          Awaiting Onboarding Dossier
                        </span>
                        <span className="text-xs text-slate-500">
                          Verified & made Live prior to matching
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Tutor Hand-off Checklist */}
                  <div className="p-3.5 rounded-xl bg-blue-50/40 border border-blue-200/80 flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg bg-blue-600 text-white flex items-center justify-center shrink-0 font-bold text-xs mt-0.5 shadow-2xs">
                      <BookOpen size={16} />
                    </div>
                    <div className="flex-1 text-xs space-y-1.5">
                      <div className="flex items-center justify-between">
                        <p className="font-bold text-blue-900">
                          {inviteMethod === "automated"
                            ? "Faculty Self-Registration Checklist (Automatic Onboarding)"
                            : "Faculty Onboarding Checklist (Manual Handoff Flow)"}
                        </p>
                        <span className="text-xs font-medium text-blue-800 bg-blue-100/70 px-2 py-0.5 rounded">
                          User-Completed
                        </span>
                      </div>

                      <p className="text-blue-800/90 leading-relaxed">
                        {inviteMethod === "automated" ? (
                          <>
                            Upon creation, an invitation email with a secure activation link is dispatched to{" "}
                            <span className="font-mono font-semibold text-slate-800">
                              {formData.email || "the faculty member's email"}
                            </span>
                            . The tutor will click the link, set their permanent password, and submit their credential dossier on first login:
                          </>
                        ) : (
                          <>
                            No automated email will be sent. Securely communicate the temporary password to{" "}
                            <span className="font-mono font-semibold text-slate-800">
                              {formData.email || "the faculty member"}
                            </span>
                            . When the tutor logs in with these credentials, they will update their password and submit their credential dossier:
                          </>
                        )}
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 pt-1 text-xs">
                        <div className="bg-white p-2 rounded-lg border border-blue-200 text-slate-700 space-y-0.5">
                          <span className="font-bold text-blue-900 block flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>{" "}
                            Step 1: Subjects *
                          </span>
                          <span className="text-slate-500">
                            Primary & secondary teaching subjects
                          </span>
                        </div>
                        <div className="bg-white p-2 rounded-lg border border-blue-200 text-slate-700 space-y-0.5">
                          <span className="font-bold text-blue-900 block flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>{" "}
                            Step 2: Degrees *
                          </span>
                          <span className="text-slate-500">
                            Degree, institution, country & year
                          </span>
                        </div>
                        <div className="bg-white p-2 rounded-lg border border-blue-200 text-slate-700 space-y-0.5">
                          <span className="font-bold text-blue-900 block flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>{" "}
                            Step 3: Experience *
                          </span>
                          <span className="text-slate-500">
                            Classroom & tutoring history (or none)
                          </span>
                        </div>
                        <div className="bg-white p-2 rounded-lg border border-blue-200 text-slate-700 space-y-0.5">
                          <span className="font-bold text-blue-900 block flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>{" "}
                            Step 4: Bio & CV *
                          </span>
                          <span className="text-slate-500">
                            Hourly rate, bio & CV PDF upload
                          </span>
                        </div>
                      </div>

                      <div className="mt-2.5 p-2.5 rounded-lg bg-white border border-purple-200 flex items-start gap-2">
                        <div className="w-4 h-4 rounded-md bg-purple-100 text-purple-700 flex items-center justify-center shrink-0 mt-0.5">
                          <Info size={12} />
                        </div>
                        <div className="text-xs text-slate-600 leading-relaxed">
                          <strong className="text-purple-900">
                            Verification & Go-Live Gate:
                          </strong>{" "}
                          Once the tutor finishes onboarding, their submission automatically routes to the{" "}
                          <span className="font-semibold text-slate-800">
                            Admin Approval Queue
                          </span>{" "}
                          (<code className="font-mono text-purple-700">/admin/tutors/approval-queue</code>). An administrator reviews their credentials, marks <code className="font-mono text-slate-800">verification_status: approved</code>, and toggles <code className="font-mono text-slate-800">is_live: true</code>. Only verified & live tutors can be matched with students or assigned to cohorts.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* SUBMIT FOOTER */}
            <div className="pt-4 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <span className="text-xs text-slate-500">
                Minimal fields: Role, Name, Email • Enrollment & Verification managed downstream
              </span>

              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/admin/users/list")}
                  className="text-xs font-semibold text-slate-700 border-slate-300 hover:bg-slate-50"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !isFormValid}
                  className="flex items-center gap-2 text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white shadow-xs"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                      Provisioning...
                    </>
                  ) : (
                    <>
                      <UserPlus size={15} />
                      {inviteMethod === "automated"
                        ? role === "student"
                          ? "Create & Dispatch Student Invite"
                          : "Create & Dispatch Faculty Invite"
                        : role === "student"
                        ? "Create Student & Save Credentials"
                        : "Create Tutor & Save Credentials"}
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>

        {/* ============================================================== */}
        {/* RIGHT COLUMN: VERTICAL LIFECYCLE PIPELINE (4 COLS, STICKY) */}
        {/* ============================================================== */}
        <div className="lg:col-span-4 sticky top-6 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl shadow-xs p-5 space-y-5">
            {/* Header */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div>
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider block">
                  Lifecycle Pipeline
                </span>
                <p className="text-xs text-slate-500 mt-0.5">
                  End-to-end academic journey
                </p>
              </div>
              <span
                className={`px-2 py-0.5 rounded text-xs font-semibold border ${
                  role === "student"
                    ? "bg-amber-50 text-amber-800 border-amber-200"
                    : "bg-blue-50 text-blue-700 border-blue-200"
                }`}
              >
                {role === "student" ? "Student Pipeline" : "Faculty Tutor Pipeline"}
              </span>
            </div>

            {/* Vertical Stepper */}
            <div className="space-y-4 relative">
              {/* Vertical connecting line */}
              <div className="absolute left-3.5 top-3.5 bottom-3.5 w-0.5 bg-slate-200 pointer-events-none"></div>

              {/* Step 1: Role & Identity Coordinates */}
              <div className="relative flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-xs ring-4 ring-white z-10">
                  1
                </div>
                <div className="flex-1 pt-0.5">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-purple-900">
                      Role & Identity
                    </h4>
                    <span className="px-1.5 py-0.2 rounded text-xs font-semibold bg-purple-100 text-purple-700">
                      Current Step
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                    Admin inputs legal name, target role ({role === "student" ? "Student" : "Faculty Tutor"}), and primary email coordinates.
                  </p>
                </div>
              </div>

              {/* Step 2: Activation & Credentials */}
              <div className="relative flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-xs ring-4 ring-white z-10">
                  2
                </div>
                <div className="flex-1 pt-0.5">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-purple-900">
                      Activation Method
                    </h4>
                    <span className="text-xs font-medium text-slate-500">
                      {inviteMethod === "automated" ? "Email Token" : "Manual Pass"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">
                    {inviteMethod === "automated"
                      ? "Automated token dispatch via Postmark with a 72-hour expiration window."
                      : "Temporary credentials generated for administrative orientation handoff."}
                  </p>
                </div>
              </div>

              {/* Step 3: User Self-Onboarding */}
              <div className="relative flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-bold text-xs shrink-0 ring-4 ring-white z-10">
                  3
                </div>
                <div className="flex-1 pt-0.5">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-slate-800">
                      User Onboarding
                    </h4>
                    <span className="text-xs font-medium text-amber-700 bg-amber-50 px-1.5 py-0.2 rounded border border-amber-200">
                      User-Gated
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    {role === "student"
                      ? "Learner logs in, sets password, and provides Date of Birth, Education Level, and Learning Goals."
                      : "Faculty member logs in, sets password, and submits Degrees, Teaching Experience, Bio, and CV."}
                  </p>
                </div>
              </div>

              {/* Step 4: Academic Activation (Role Adaptive) */}
              <div className="relative flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center font-bold text-xs shrink-0 ring-4 ring-white z-10">
                  4
                </div>
                <div className="flex-1 pt-0.5">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-slate-800">
                      {role === "student"
                        ? "Course Enrollment"
                        : "Verification & Go-Live"}
                    </h4>
                    <span className="text-xs font-medium text-purple-700 bg-purple-50 px-1.5 py-0.2 rounded border border-purple-200">
                      {role === "student" ? "Catalog / Manual" : "Approval Queue"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    {role === "student"
                      ? "Student self-enrolls in courses or admin initiates Manual Enrollment (/admin/enrollments/create) on their behalf."
                      : "Admin audits dossier in Approval Queue (/admin/tutors/approval-queue), marks approved, and enables is_live: true."}
                  </p>
                </div>
              </div>
            </div>

            {/* Live Profile Telemetry Card */}
            <div className="pt-4 border-t border-slate-100">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2.5">
                Live Profile Summary
              </span>
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2">
                <div className="flex items-center gap-2.5">
                  <div
                    className={`w-9 h-9 rounded-lg flex items-center justify-center font-bold text-xs text-white shadow-2xs ${
                      role === "student" ? "bg-amber-500" : "bg-blue-600"
                    }`}
                  >
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-bold text-slate-900 block truncate">
                      {formData.firstName || formData.lastName
                        ? `${formData.firstName} ${formData.lastName}`
                        : "Pending Identity Entry"}
                    </span>
                    <span className="text-xs text-slate-500 font-mono block truncate">
                      {formData.email || "no-email@domain.com"}
                    </span>
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-200/80 flex items-center justify-between text-xs">
                  <span className="text-slate-500">Dispatch Route</span>
                  <span className="font-semibold text-slate-800">
                    {inviteMethod === "automated" ? "Automated Token" : "Manual Pass"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">Initial Status</span>
                  <span className="font-semibold text-amber-700">
                    Onboarding Pending
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateUser;
